import { useState } from 'react';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Share2, Copy, Eye, Lock, Calendar, Trash2, Power, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface PublicShareDialogProps {
  boardId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PublicShareDialog({ boardId, open, onOpenChange }: PublicShareDialogProps) {
  const [expiresInDays, setExpiresInDays] = useState<number | null>(null);
  const [viewOnly, setViewOnly] = useState(true);
  const [requirePassword, setRequirePassword] = useState(false);
  const [password, setPassword] = useState('');

  const utils = trpc.useUtils();

  const { data: shares } = trpc.taskManager.publicShares.byBoard.useQuery(
    { boardId },
    { enabled: open }
  );

  const createShare = trpc.taskManager.publicShares.create.useMutation({
    onSuccess: (data: any) => {
      toast.success('Link de compartilhamento criado');
      utils.taskManager.publicShares.byBoard.invalidate({ boardId });
      
      // Copiar link para clipboard
      const shareUrl = `${window.location.origin}/shared/${data.insertId}`;
      navigator.clipboard.writeText(shareUrl);
      toast.info('Link copiado para área de transferência');
    },
    onError: (error) => {
      toast.error(`Erro ao criar compartilhamento: ${error.message}`);
    },
  });

  const deactivateShare = trpc.taskManager.publicShares.deactivate.useMutation({
    onSuccess: () => {
      toast.success('Compartilhamento desativado');
      utils.taskManager.publicShares.byBoard.invalidate({ boardId });
    },
  });

  const deleteShare = trpc.taskManager.publicShares.delete.useMutation({
    onSuccess: () => {
      toast.success('Compartilhamento deletado');
      utils.taskManager.publicShares.byBoard.invalidate({ boardId });
    },
  });

  const handleCreateShare = () => {
    const expiresAt = expiresInDays
      ? new Date(Date.now() + expiresInDays * 24 * 60 * 60 * 1000)
      : undefined;

    createShare.mutate({
      boardId,
      expiresAt,
      viewOnly,
      requirePassword,
      password: requirePassword ? password : undefined,
    });
  };

  const copyShareLink = (shareToken: string) => {
    const shareUrl = `${window.location.origin}/shared/${shareToken}`;
    navigator.clipboard.writeText(shareUrl);
    toast.success('Link copiado');
  };

  const openShareLink = (shareToken: string) => {
    const shareUrl = `${window.location.origin}/shared/${shareToken}`;
    window.open(shareUrl, '_blank');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Compartilhamento Público
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Criar novo compartilhamento */}
          <div className="border rounded-lg p-4 space-y-4">
            <h4 className="font-medium">Criar Novo Link</h4>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Expira em (dias)</Label>
                <Input
                  type="number"
                  placeholder="Nunca expira"
                  value={expiresInDays || ''}
                  onChange={(e) => setExpiresInDays(e.target.value ? parseInt(e.target.value) : null)}
                  min={1}
                />
              </div>

              <div className="space-y-2">
                <Label>Senha (opcional)</Label>
                <Input
                  type="password"
                  placeholder="Deixe vazio para sem senha"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={!requirePassword}
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Eye className="h-4 w-4 text-muted-foreground" />
                <Label>Apenas visualização</Label>
              </div>
              <Switch checked={viewOnly} onCheckedChange={setViewOnly} />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Lock className="h-4 w-4 text-muted-foreground" />
                <Label>Proteger com senha</Label>
              </div>
              <Switch checked={requirePassword} onCheckedChange={setRequirePassword} />
            </div>

            <Button
              onClick={handleCreateShare}
              disabled={createShare.isPending || (requirePassword && !password)}
              className="w-full"
            >
              {createShare.isPending ? 'Criando...' : 'Criar Link de Compartilhamento'}
            </Button>
          </div>

          {/* Links existentes */}
          <div>
            <h4 className="font-medium mb-3">Links Ativos</h4>
            <ScrollArea className="max-h-64">
              <div className="space-y-2">
                {!shares || shares.length === 0 ? (
                  <div className="text-center text-muted-foreground py-8">
                    Nenhum link de compartilhamento criado
                  </div>
                ) : (
                  shares.map((share: any) => (
                    <div
                      key={share.id}
                      className="border rounded-lg p-3 space-y-2"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {share.active ? (
                              <Badge variant="default" className="text-xs">Ativo</Badge>
                            ) : (
                              <Badge variant="secondary" className="text-xs">Desativado</Badge>
                            )}
                            {share.view_only && (
                              <Badge variant="outline" className="text-xs">
                                <Eye className="h-3 w-3 mr-1" />
                                Apenas leitura
                              </Badge>
                            )}
                            {share.require_password && (
                              <Badge variant="outline" className="text-xs">
                                <Lock className="h-3 w-3 mr-1" />
                                Protegido
                              </Badge>
                            )}
                          </div>

                          <div className="text-xs text-muted-foreground space-y-1">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-3 w-3" />
                              Criado {formatDistanceToNow(new Date(share.created_at), {
                                addSuffix: true,
                                locale: ptBR,
                              })}
                            </div>
                            {share.expires_at && (
                              <div className="flex items-center gap-2">
                                <Calendar className="h-3 w-3" />
                                Expira {formatDistanceToNow(new Date(share.expires_at), {
                                  addSuffix: true,
                                  locale: ptBR,
                                })}
                              </div>
                            )}
                            <div>Acessos: {share.access_count}</div>
                          </div>
                        </div>

                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyShareLink(share.share_token)}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openShareLink(share.share_token)}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                          {share.active && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deactivateShare.mutate({ shareId: share.id })}
                            >
                              <Power className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteShare.mutate({ shareId: share.id })}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
